<template>
  <div :class="['profile-profile', { page }]">
    <spinner v-if="!loaded" />

    <template v-else>
      <section :class="['profile-header', { page, 'profile-header--no-image': !headerProfileImage }]">
        <div class="profile-header-actions">
          <align justify="space-between">
            <align small-gap>
              <icon-button
                v-if="!page"
                tooltip="Close profile"
                icon="close"
                @click="close"
              />

              <icon-button
                v-if="!page && profileId"
                tooltip="View full profile"
                class="medium-up"
                icon="fullscreen"
                @click="fullscreen"
              />
            </align>

            <dropdown
              v-if="isStaff && profileMenuOptions.length"
              align="right"
              :active="profileMenuOpen"
              @close="profileMenuOpen = false"
            >
              <icon-button
                icon="more_vert"
                @click="profileMenuOpen = !profileMenuOpen"
              />

              <template #menu>
                <dropdown-menu
                  :options="profileMenuOptions"
                  @select="handleProfileMenuSelect"
                />
              </template>
            </dropdown>
          </align>
        </div>

        <div v-if="headerProfileImage" class="profile-header-image">
          <card-profile-images :card="{ image: headerProfileImage }" :page="page" />
        </div>

        <div class="profile-header-info">
          <div class="profile-header-title">
            <h3>{{ profile.label }}</h3>

            <p class="profile-graders">
              <span
                v-for="graderItem in profile.graders"
                :key="graderItem.grader"
                :class="[
                  'profile-grader-logo-pill',
                  {
                    'profile-grader-logo-pill--interactive': profile.graders.length > 1,
                    'profile-grader-logo-pill--selected':
                      profile.graders.length > 1 && salesStripGrader === graderItem.grader,
                  },
                ]"
                :role="profile.graders.length > 1 ? 'button' : undefined"
                :tabindex="profile.graders.length > 1 ? 0 : -1"
                :aria-pressed="profile.graders.length > 1 ? salesStripGrader === graderItem.grader : undefined"
                @click="onHeaderGraderClick(graderItem)"
                @keyup.enter="onHeaderGraderClick(graderItem)"
                @keyup.space.prevent="onHeaderGraderClick(graderItem)"
              >
                <img
                  v-if="getGraderLogoUrl(graderItem.grader)"
                  class="profile-grader-logo-img"
                  :src="getGraderLogoUrl(graderItem.grader)"
                  :alt="graderItem.label"
                />
                <pill v-else>
                  {{ graderItem.label }}
                </pill>
              </span>

              <icon-button
                v-if="profile.excludeFromMatches === true"
                class="profile-excluded-indicator"
                :class="{ 'profile-excluded-indicator--staff-action': isStaff }"
                icon="visibility_off"
                :tooltip="excludeFromMatchesIndicatorTooltip"
                :loading="restoringToMatchQueue"
                @click="onExcludeFromMatchesIndicatorClick"
              />
            </p>
          </div>

          <div :class="['stats-grid', 'profile-header-stats', { page }]">
            <stat-item
              class="profile-stat"
              label="Total Population"
              :value="$filters.number(profile.totalPopulation)"
            />

            <stat-item
              class="profile-stat"
              label="Total Gems"
              :value="$filters.number(profile.totalGems)"
            />

            <stat-item
              class="profile-stat"
              label="Gem Rate"
              :value="$filters.percentage(profile.gemRate)"
            />
          </div>

          <div class="profile-attributes">
            <div class="profile-attribute">
              <span class="profile-attribute-label">Category</span>
              <span class="profile-attribute-value">{{ profile.category }}</span>
            </div>

            <div class="profile-attribute">
              <span class="profile-attribute-label">Year</span>
              <span class="profile-attribute-value">{{ profile.year }}</span>
            </div>

            <div class="profile-attribute">
              <span class="profile-attribute-label">Set</span>
              <span class="profile-attribute-value">{{ profile.set }}</span>
            </div>

            <div class="profile-attribute">
              <span class="profile-attribute-label">Number</span>
              <span class="profile-attribute-value">{{ profile.number }}</span>
            </div>

            <div class="profile-attribute">
              <span class="profile-attribute-label">Subject</span>
              <span class="profile-attribute-value">{{ profile.subject }}</span>
            </div>

            <div v-if="profile.variation" class="profile-attribute">
              <span class="profile-attribute-label">Variation</span>
              <span class="profile-attribute-value">{{ profile.variation }}</span>
            </div>
          </div>

          <div v-if="profileId" class="profile-view-all-sales-cta">
            <btn kind="secondary" block @click="goToSalesProfileOnly">
              View All Sales
            </btn>
          </div>
        </div>
      </section>

      <section
        v-if="showProfileSalesSection"
        ref="profileSalesSection"
        class="profile-sales-row"
      >
        <align justify="space-between">
          <h4 class="short-text secondary-font">
            Sales
          </h4>

          <icon-link @click="goToSales">
            View All Sales
          </icon-link>
        </align>

        <align
          v-if="profile && salesGraderDropdownOptions.length"
          class="profile-sales-filters"
          small-gap
        >
          <pill-dropdown
            :value="salesStripGrader"
            align="left"
            :options="salesGraderDropdownOptions"
            class="profile-sales-filter-dropdown"
            @input="onSalesGraderChange"
          />
          <pill-dropdown
            v-if="salesGradeDropdownOptions.length"
            :value="salesStripGrade"
            align="left"
            :options="salesGradeDropdownOptions"
            class="profile-sales-filter-dropdown"
            @input="onSalesGradeChange"
          />
          <pill-dropdown
            v-if="salesPopulationTypeDropdownOptions.length > 1"
            :value="salesStripPopulationType"
            align="left"
            :options="salesPopulationTypeDropdownOptions"
            class="profile-sales-filter-dropdown"
            @input="onSalesPopulationTypeChange"
          />
        </align>

        <div v-if="profileSalesForList.length" class="profile-recent-sales">
          <sales-list-item
            v-for="(item, i) in profileSalesForList"
            :key="item.id || i"
            :item="item"
            as="a"
            :href="buildExternalSaleOpenUrl(item)"
            target="_blank"
            rel="noopener noreferrer"
          >
            <align small-gap>
              <verified-icon v-if="item.cardId || item.memberVerified" />
            </align>
          </sales-list-item>
        </div>
      </section>

      <section class="graders-section">
        <div class="graders-section-header">
          <h4 class="short-text secondary-font">
            Population Report
          </h4>
          <pill-dropdown
            :value="populationReportValueDisplay"
            align="left"
            :options="[{ value: 'clValue', label: 'CL Value' }, { value: 'lastSold', label: 'Last sold' }]"
            class="population-report-value-dropdown"
            @input="populationReportValueDisplay = $event"
          />
        </div>

        <div v-for="graderItem in profile.graders" :key="graderItem.grader" class="grader-section">
          <div class="grader-section-header">
            <div class="grader-section-header-brand">
              <span v-if="getGraderLogoUrl(graderItem.grader)" class="profile-grader-logo-pill">
                <img
                  class="profile-grader-logo-img"
                  :src="getGraderLogoUrl(graderItem.grader)"
                  :alt="graderItem.label"
                />
              </span>
              <h5 v-else class="short-text secondary-font">
                {{ graderItem.label }}
              </h5>
            </div>
            <pill-dropdown
              v-if="populationTypeOptions(graderItem).length > 1"
              :value="selectedPopulationTypeByGrader[graderItem.grader] || 'base'"
              align="left"
              :options="populationTypeOptions(graderItem)"
              class="grader-population-type-dropdown"
              @input="setPopulationType(graderItem.grader, $event)"
            />
          </div>

          <div class="grader-stats">
            <stat-item
              label="Total Population"
              :value="$filters.number(graderItem.totalPopulation)"
            />
            <stat-item
              label="Total Gems"
              :value="$filters.number(graderItem.totalGems)"
            />
            <stat-item
              label="Gem Rate"
              :value="$filters.percentage(graderItem.gemRate)"
            />
          </div>

          <div class="grades-grid">
            <div
              v-for="gradeItem in gradesForGrader(graderItem)"
              :key="gradeItem.grade"
              :class="['grade-item', { selected: isPopulationGradeSelected(graderItem, gradeItem) }, { 'no-sales': !gradeItem.price }]"
              @click="onPopulationReportGradeClick(graderItem, gradeItem)"
            >
              <span class="grade-label">{{ gradeItem.label }}</span>
              <div class="grade-metrics">
                <div
                  class="grade-metric grade-metric--price"
                >
                  <span class="grade-metric-label">{{ populationReportValueDisplay === 'clValue' ? 'CL Value' : 'Last sold' }}</span>
                  <span class="grade-metric-value">
                    {{ populationReportValueDisplay === 'clValue'
                      ? (gradeItem.clValue != null ? $filters.currency(gradeItem.clValue) : '—')
                      : (gradeItem.price != null && gradeItem.price > 0 ? $filters.currency(gradeItem.price) : '—') }}
                  </span>
                </div>
                <div
                  class="grade-metric grade-metric--pop"
                >
                  <span class="grade-metric-label">Pop</span>
                  <span class="grade-metric-value">{{ $filters.number(gradeItem.value) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div class="index-container" @click="openIndex">
        <index-list-item :index="profile.index" />
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import firebase from 'firebase/compat/app';

import CardProfileImages from '@cardladder/shared/components/CardProfileImages';
import IndexListItem from '@cardladder/shared/components/IndexListItem';
import PillDropdown from '@cardladder/shared/components/PillDropdown';
import StatItem from '@cardladder/shared/components/StatItem';
import SalesListItem from '@cardladder/shared/components/SalesListItem';
import VerifiedIcon from '@cardladder/shared/components/VerifiedIcon';

import { LISTING_TYPE_OPTIONS, SELLING_PLATFORM_OPTIONS } from '@cardladder/shared/constants';
import { filterProfileSalesForSelection } from '@cardladder/shared/utils/profileSales.util';
import { buildExternalSaleOpenUrl } from '@cardladder/shared/utils/sales.util';
import { graderLogoSrc as getGraderLogoUrl } from '@/utils/graderLogos';

const PROFILE_CACHE_TTL_MS = 10 * 60 * 1000;
const profileCache = new Map(); // profileId -> { data, cachedAt }

export default {
  components: {
    CardProfileImages,
    IndexListItem,
    PillDropdown,
    StatItem,
    SalesListItem,
    VerifiedIcon,
  },

  props: {
    profileId: String,
    page: Boolean,
  },

  data() {
    return {
      profile: null,
      loading: true,
      loaded: false,
      selectedGrader: null,
      selectedGrade: null,
      selectedPopulationType: 'base',
      selectedPopulationTypeByGrader: {},
      /** Drives last-5 sales only; not updated by population-report type dropdown (only grade click + sales row + load/route). */
      salesStripGrader: null,
      salesStripGrade: null,
      salesStripPopulationType: 'base',
      populationReportValueDisplay: 'clValue',
      profileMenuOpen: false,
      restoringToMatchQueue: false,
    };
  },

  created() {
    this.load();
  },

  watch: {
    profileId(to, from) {
      if (to !== from) {
        this.load();
      }
    },

    '$route.query.profileGrader': function profileGraderRouteChanged(to, from) {
      if (to !== from && this.profile) {
        this.applySelectionFromRoute();
      }
    },

    '$route.query.profileGrade': function profileGradeRouteChanged(to, from) {
      if (to !== from && this.profile) {
        this.applySelectionFromRoute();
      }
    },

    '$route.query.profilePopulationType': function profilePopulationTypeRouteChanged(to, from) {
      if (to !== from && this.profile) {
        this.applySelectionFromRoute();
      }
    },
  },

  computed: {
    ...mapGetters({
      isStaff: 'isStaff',
    }),

    headerProfileImage() {
      const p = this.profile;
      if (!p) return null;
      const grader = this.salesStripGrader;
      const grade = this.salesStripGrade;
      const pop = this.salesStripPopulationType || 'base';
      const map = p.comboImages;
      if (grader && grade != null && map && typeof map === 'object') {
        const key = `${String(grader).toLowerCase()}|${String(grade)}|${pop}`;
        if (map[key]) return map[key];
      }
      return p.image;
    },

    profileMenuOptions() {
      if (!this.profile) return [];

      const options = [];

      if (this.profile.hasMatches) {
        options.push({ value: 'unmatch', label: 'Unmatch profile' });
      } else {
        options.push({ value: 'delete', label: 'Delete profile' });
      }

      return options;
    },

    populationTypeOptions() {
      return (graderItem) => {
        const opts = [{ value: 'base', label: 'Base' }];
        if (!graderItem) return opts;
        if (Array.isArray(graderItem.qualifierGrades)) opts.push({ value: 'qualifier', label: 'Qualifier' });
        if (Array.isArray(graderItem.autoGrades)) opts.push({ value: 'autograph', label: 'Autograph' });
        return opts;
      };
    },

    gradesForGrader() {
      return (graderItem) => {
        if (!graderItem?.grader) return [];
        const type = this.selectedPopulationTypeByGrader[graderItem.grader] || 'base';
        if (type === 'qualifier') return graderItem.qualifierGrades || [];
        if (type === 'autograph') return graderItem.autoGrades || [];
        return graderItem.grades || [];
      };
    },

    /** Full /profiles/:id page only — not the side panel; also hidden on Sales History where the list is redundant. */
    showProfileSalesSection() {
      return Boolean(this.page) && this.$route.path !== '/sales-history';
    },

    excludeFromMatchesIndicatorTooltip() {
      if (this.isStaff) {
        return 'Excluded from the matching queue. Click to include again (asks for confirmation).';
      }
      return 'Excluded from the matching queue (single-grader / won\'t match).';
    },

    salesGraderDropdownOptions() {
      if (!this.profile?.graders?.length) return [];
      return this.profile.graders.map((g) => ({ value: g.grader, label: g.label }));
    },

    salesPopulationTypeDropdownOptions() {
      const graderItem = this.profile?.graders?.find((g) => g.grader === this.salesStripGrader);
      return this.populationTypeOptions(graderItem);
    },

    salesGradeDropdownOptions() {
      const graderItem = this.profile?.graders?.find((g) => g.grader === this.salesStripGrader);
      if (!graderItem) return [];
      const list = this.gradesListForPopulationType(graderItem, this.salesStripPopulationType);
      return list.map((g) => ({ value: g.grade, label: g.label }));
    },

    profileSalesForList() {
      const raw = this.profile?.sales;
      if (!Array.isArray(raw) || !raw.length) return [];
      if (!this.salesStripGrader || this.salesStripGrade == null || this.salesStripGrade === '') return [];

      const pop = this.salesStripPopulationType;
      const graderItem = this.profile?.graders?.find((g) => g.grader === this.salesStripGrader);
      const supportsAutographPopulation = Array.isArray(graderItem?.autoGrades) && graderItem.autoGrades.length > 0;
      const filtered = filterProfileSalesForSelection(
        raw,
        this.salesStripGrader,
        this.salesStripGrade,
        pop,
        5,
        supportsAutographPopulation,
      );

      return filtered.map((hit) => {
        const platform = hit.platform && SELLING_PLATFORM_OPTIONS.find(({ value }) => value === hit.platform)
          ? SELLING_PLATFORM_OPTIONS.find(({ value }) => value === hit.platform).label
          : hit.platform || '';
        const listingType = hit.listingType && LISTING_TYPE_OPTIONS.find(({ value }) => value === hit.listingType)
          ? LISTING_TYPE_OPTIONS.find(({ value }) => value === hit.listingType).label
          : hit.listingType || '';

        return {
          ...hit,
          platform,
          listingType,
          date: hit.date ? new Date(hit.date) : '',
        };
      });
    },

  },

  methods: {
    getGraderLogoUrl,

    buildExternalSaleOpenUrl,

    gradesListForPopulationType(graderData, populationType) {
      if (!graderData) return [];
      const type = populationType || 'base';
      if (type === 'qualifier') return graderData.qualifierGrades || [];
      if (type === 'autograph') return graderData.autoGrades || [];
      return graderData.grades || [];
    },

    /**
     * Highlight when this cell matches the sales strip (last-5) grader/grade/pop and the grid’s population dropdown.
     */
    isPopulationGradeSelected(graderItem, gradeItem) {
      if (!this.salesStripGrader || this.salesStripGrade == null) return false;
      if (this.salesStripGrader !== graderItem.grader) return false;
      if (String(this.salesStripGrade) !== String(gradeItem.grade)) return false;
      const popShown = this.selectedPopulationTypeByGrader[graderItem.grader] || 'base';
      return String(this.salesStripPopulationType) === String(popShown);
    },

    /** Base grades only: prefer explicit `grade` prop when valid, else grade with highest pop (`value`). */
    defaultHeaderGradeForBaseGrades(graderData) {
      const grades = graderData?.grades ?? [];
      if (this.grade && grades.some((g) => g.grade === this.grade)) {
        return this.grade;
      }
      const withPop = grades.filter((g) => g.value != null && Number(g.value) > 0);
      if (withPop.length) {
        return withPop.reduce((best, g) => (Number(g.value) > Number(best.value) ? g : best)).grade;
      }
      const withPrice = grades.filter((g) => g.price != null && g.price > 0);
      if (withPrice.length) return withPrice[0].grade;
      return grades[0]?.grade ?? null;
    },

    defaultGradeInList(list) {
      if (!list || !list.length) return null;
      const withPop = list.filter((g) => g.value != null && Number(g.value) > 0);
      if (withPop.length) {
        return withPop.reduce((best, g) => (Number(g.value) > Number(best.value) ? g : best)).grade;
      }
      const withPrice = list.filter((g) => g.price != null && g.price > 0);
      if (withPrice.length) return withPrice[0].grade;
      return list[0]?.grade ?? null;
    },

    profileCacheKeyForId(internalProfileId) {
      const payload = this.getSalesFilterPayloadForRequest();
      if (!payload) return `${internalProfileId}:default`;
      return `${internalProfileId}:${payload.salesGrader}:${String(payload.salesGrade)}:${payload.salesPopulationType}`;
    },

    profileCacheKey() {
      return this.profileCacheKeyForId(this.profileId);
    },

    /**
     * When a profile doc is a duplicate that points at a canonical profile, return the canonical id for data loads.
     */
    async resolveCanonicalProfileId(profileId) {
      try {
        const snap = await firebase.firestore().collection('profiles').doc(profileId).get();
        if (!snap.exists) return profileId;
        const c = snap.data().canonicalProfileId;
        if (!c || typeof c !== 'string' || c === profileId) return profileId;
        const cSnap = await firebase.firestore().collection('profiles').doc(c).get();
        return cSnap.exists ? c : profileId;
      } catch (e) {
        return profileId;
      }
    },

    getSalesFilterPayloadForRequest() {
      const q = this.$route.query;
      if (String(q.profileId || '') !== String(this.profileId || '')) return null;
      if (!q.profileGrader || q.profileGrade === undefined || q.profileGrade === null || q.profileGrade === '') {
        return null;
      }
      const pop = q.profilePopulationType && ['base', 'qualifier', 'autograph'].includes(q.profilePopulationType)
        ? q.profilePopulationType
        : 'base';
      return {
        salesGrader: q.profileGrader,
        salesGrade: q.profileGrade,
        salesPopulationType: pop,
      };
    },

    applySalesFilterFromResponse(salesFilter) {
      if (!salesFilter || !salesFilter.grader) return;
      const pop = salesFilter.populationType || 'base';
      this.selectedGrader = salesFilter.grader;
      const graderData = this.profile?.graders?.find((g) => g.grader === salesFilter.grader);
      const list = this.gradesListForPopulationType(graderData, pop);
      const match = list.find((g) => String(g.grade) === String(salesFilter.grade));
      let grade = match ? match.grade : salesFilter.grade;
      if (grade == null && list.length) {
        grade = this.defaultGradeInList(list);
      }
      this.selectedGrade = grade;
      this.selectedPopulationType = pop;
      this.$set(this.selectedPopulationTypeByGrader, salesFilter.grader, pop);
      this.commitSalesStripFromSelection();
    },

    commitSalesStripFromSelection() {
      if (!this.selectedGrader || this.selectedGrade == null || this.selectedGrade === '') return;
      const pop = this.selectedPopulationTypeByGrader[this.selectedGrader]
        || this.selectedPopulationType
        || 'base';
      this.salesStripGrader = this.selectedGrader;
      this.salesStripGrade = this.selectedGrade;
      this.salesStripPopulationType = pop;
      this.syncProfileSalesFilterToSelection();
    },

    syncProfileSalesFilterToSelection() {
      if (!this.profile || !this.salesStripGrader || this.salesStripGrade == null || this.salesStripGrade === '') return;
      const pop = this.salesStripPopulationType || 'base';
      this.$set(this.profile, 'salesFilter', {
        grader: this.salesStripGrader,
        grade: String(this.salesStripGrade),
        populationType: pop,
      });
    },

    /** Older cached payloads or responses without `salesFilter`: keep header / population UI consistent. */
    applyDefaultSelectionWhenSalesFilterMissing() {
      if (this.selectedGrader || !this.profile?.graderList?.length) return;
      this.selectedGrader = this.profile.graderList[0];
      const graderData = this.profile.graders?.find((g) => g.grader === this.selectedGrader);
      this.selectedGrade = this.defaultHeaderGradeForBaseGrades(graderData);
      this.selectedPopulationType = 'base';
      this.selectedPopulationTypeByGrader = {};
      this.$set(this.selectedPopulationTypeByGrader, this.selectedGrader, 'base');
      this.commitSalesStripFromSelection();
    },

    onHeaderGraderClick(graderItem) {
      if (!this.profile?.graders || this.profile.graders.length < 2 || !graderItem?.grader) return;
      if (this.salesStripGrader === graderItem.grader) return;

      this.onSalesGraderChange(graderItem.grader);

      if (this.showProfileSalesSection) {
        this.$nextTick(() => {
          this.scrollSalesSectionIntoView();
        });
      }
    },

    onSalesGraderChange(grader) {
      this.selectedGrader = grader;
      const graderData = this.profile.graders.find((g) => g.grader === grader);
      const pop = this.selectedPopulationTypeByGrader[grader] || 'base';
      this.selectedPopulationType = pop;
      const list = this.gradesListForPopulationType(graderData, pop);
      this.selectedGrade = this.defaultGradeInList(list);
      this.$set(this.selectedPopulationTypeByGrader, grader, pop);
      this.commitSalesStripFromSelection();
      this.syncSelectionToRoute();
    },

    onSalesPopulationTypeChange(pop) {
      if (!this.salesStripGrader) return;
      this.selectedGrader = this.salesStripGrader;
      this.setPopulationType(this.salesStripGrader, pop, { commitSalesStrip: true });
    },

    onSalesGradeChange(grade) {
      this.selectedGrader = this.salesStripGrader;
      this.selectedGrade = grade;
      this.salesStripGrade = grade;
      this.syncProfileSalesFilterToSelection();
      this.syncSelectionToRoute();
    },

    async load() {
      if (!this.profileId) {
        this.loading = false;
        return;
      }

      this.loading = true;
      this.profile = null;

      try {
        const fetchId = await this.resolveCanonicalProfileId(this.profileId);
        if (fetchId !== this.profileId && this.page) {
          this.loading = false;
          await this.$router.replace({ path: `/profiles/${fetchId}`, query: { ...this.$route.query } }).catch(() => {});
          return;
        }

        const cacheKey = this.profileCacheKeyForId(fetchId);
        const cached = profileCache.get(cacheKey);
        const isFresh = cached && (Date.now() - cached.cachedAt) < PROFILE_CACHE_TTL_MS;

        if (isFresh && cached?.data) {
          this.profile = cached.data;
          this.applySalesFilterFromResponse(this.profile.salesFilter);
          this.applyDefaultSelectionWhenSalesFilterMissing();
          this.applySelectionFromRoute();
          this.loading = false;
          this.loaded = true;
          return;
        }

        const { data: profileData } = await firebase.functions().httpsCallable('httpprofile')({
          profileId: fetchId,
        });

        this.profile = profileData;
        profileCache.set(cacheKey, { data: profileData, cachedAt: Date.now() });

        this.applySalesFilterFromResponse(profileData.salesFilter);
        this.applyDefaultSelectionWhenSalesFilterMissing();
        this.applySelectionFromRoute();
      } catch (e) {
        this.$toasted.error('Error loading profile');
        this.error = e.message || 'Failed to load profile';
      }

      this.loading = false;
      this.loaded = true;
    },

    setPopulationType(grader, value, options = {}) {
      const v = value || 'base';
      const commitSalesStrip = options.commitSalesStrip === true;
      this.$set(this.selectedPopulationTypeByGrader, grader, v);
      if (grader === this.selectedGrader) {
        this.selectedPopulationType = v;
        const graderData = this.profile?.graders?.find((g) => g.grader === grader);
        const list = this.gradesListForPopulationType(graderData, v);
        const stillValid = list.some((g) => String(g.grade) === String(this.selectedGrade));
        if (!stillValid && list.length) {
          const gradesWithPrice = list.filter((g) => g.price != null && g.price > 0);
          this.selectedGrade = gradesWithPrice[0]?.grade ?? list[0]?.grade ?? null;
        }
        if (this.page) {
          this.syncSelectionToRoute();
        } else if (commitSalesStrip && this.selectedGrader && this.selectedGrade != null) {
          this.syncSalesHistoryFiltersForGrade(this.selectedGrader, this.selectedGrade, v);
        }
      }
      if (commitSalesStrip) {
        this.commitSalesStripFromSelection();
      }
    },

    /**
     * Pipe-encoded `filters` + profile selection keys for Sales History (same shape as panel sync).
     */
    buildSalesHistoryQueryForGrade(grader, grade, populationType = 'base') {
      if (!this.profileId || !this.profile?.graders || !grader || grade == null || grade === '') {
        return null;
      }

      const activeGraderObject = this.profile.graders.find((g) => g.grader === grader);
      if (!activeGraderObject?.graderId) return null;

      const filtersObject = {
        profileId: `${grader}-${activeGraderObject.graderId}`,
        grader,
        grade,
      };

      if (populationType === 'qualifier') {
        filtersObject.hasQualifier = true;
      } else if (populationType === 'autograph') {
        filtersObject.hasAutographGrade = true;
      }

      const filters = Object.keys(filtersObject).map((key) => `${key}:${filtersObject[key]}`).join('|');

      const query = {
        filters,
        profileId: this.profileId,
        profileGrader: grader,
        profileGrade: grade,
      };

      if (populationType && populationType !== 'base') {
        query.profilePopulationType = populationType;
      }

      return query;
    },

    mergeSalesHistoryQueryIntoRoute(query, populationType) {
      const merged = { ...this.$route.query, ...query };
      if (!populationType || populationType === 'base') {
        delete merged.profilePopulationType;
      }
      return merged;
    },

    /**
     * Updates Sales History URL when the sales strip or a population-report grade click commits
     * a new selection. Not used for population-report type dropdown alone (Qualifier/Autograph).
     */
    syncSalesHistoryFiltersForGrade(grader, grade, populationType = 'base') {
      const partial = this.buildSalesHistoryQueryForGrade(grader, grade, populationType);
      if (!partial) return;

      const merged = this.mergeSalesHistoryQueryIntoRoute(partial, populationType);
      this.$router.replace({ path: this.$route.path, query: merged }).catch(() => {});
    },

    onPopulationReportGradeClick(graderItem, gradeItem) {
      if (!gradeItem.price || gradeItem.price <= 0) return;

      const populationType = this.selectedPopulationTypeByGrader[graderItem.grader] || 'base';
      this.selectedGrader = graderItem.grader;
      this.selectedGrade = gradeItem.grade;
      this.selectedPopulationType = populationType;
      this.$set(this.selectedPopulationTypeByGrader, graderItem.grader, populationType);
      this.commitSalesStripFromSelection();

      if (this.page) {
        this.syncSelectionToRoute();
        this.$nextTick(() => {
          this.scrollSalesSectionIntoView();
        });
      } else {
        this.syncSalesHistoryFiltersForGrade(graderItem.grader, gradeItem.grade, populationType);
      }
    },

    scrollSalesSectionIntoView() {
      const el = this.$refs.profileSalesSection;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const scrollTop = window.scrollY ?? document.documentElement.scrollTop;
      const elementTop = rect.top + scrollTop;
      const offset = 96;
      window.scrollTo({
        top: Math.max(0, elementTop - offset),
        behavior: 'smooth',
      });
    },

    openIndex() {
      if (this.profile?.index?.source === 'players') {
        this.$router.push(`/players/${this.profile.index.id}`);
      } else if (this.profile?.index?.source === 'indexes') {
        this.$router.push(`/indexes/${this.profile.index.id}`);
      }
    },

    goToSalesProfileOnly() {
      if (!this.profileId) return;

      const filtersObject = { profileId: this.profileId };
      const filters = Object.keys(filtersObject).map((key) => `${key}:${filtersObject[key]}`).join('|');

      this.$router.push({
        path: '/sales-history',
        query: {
          filters,
          profileId: this.profileId,
          sort: 'date',
          direction: 'desc',
        },
      });
    },

    goToSales() {
      const pop = this.salesStripPopulationType;
      const partial = this.buildSalesHistoryQueryForGrade(this.salesStripGrader, this.salesStripGrade, pop);

      if (partial) {
        const query = {
          ...partial,
          sort: 'date',
          direction: 'desc',
        };
        if (!pop || pop === 'base') {
          delete query.profilePopulationType;
        }
        this.$router.push({ path: '/sales-history', query });
        return;
      }

      this.goToSalesProfileOnly();
    },

    applySelectionFromRoute() {
      if (!this.profile) return;
      if (this.$route?.query?.profileId !== this.profileId) return;

      const routeGrader = this.$route?.query?.profileGrader || null;
      const routeGrade = this.$route?.query?.profileGrade || null;
      const routePop = this.$route?.query?.profilePopulationType || null;
      const validPops = ['base', 'qualifier', 'autograph'];

      if (routeGrader && this.selectedGrader !== routeGrader) {
        const exists = this.profile?.graderList?.includes(routeGrader)
          || this.profile?.graders?.some((g) => g.grader === routeGrader);
        if (exists) {
          this.selectedGrader = routeGrader;
        }
      }

      if (routePop && validPops.includes(routePop) && this.selectedPopulationType !== routePop) {
        this.selectedPopulationType = routePop;
        if (this.selectedGrader) {
          this.$set(this.selectedPopulationTypeByGrader, this.selectedGrader, routePop);
        }
      }

      if (routeGrade != null && routeGrade !== '' && String(this.selectedGrade) !== String(routeGrade)) {
        const graderData = this.profile?.graders?.find((g) => g.grader === this.selectedGrader);
        const popForGrade = (routePop && validPops.includes(routePop)) ? routePop : this.selectedPopulationType;
        const grades = this.gradesListForPopulationType(graderData, popForGrade);
        const match = grades.find((g) => String(g.grade) === String(routeGrade));
        if (match) {
          this.selectedGrade = match.grade;
        }
      }

      this.commitSalesStripFromSelection();
    },

    syncSelectionToRoute() {
      if (this.$route?.query?.profileId !== this.profileId) return;

      const query = { ...this.$route.query };

      if (this.selectedGrader) query.profileGrader = this.selectedGrader;
      else delete query.profileGrader;

      if (this.selectedGrade) query.profileGrade = this.selectedGrade;
      else delete query.profileGrade;

      if (this.selectedPopulationType && this.selectedPopulationType !== 'base') {
        query.profilePopulationType = this.selectedPopulationType;
      } else {
        delete query.profilePopulationType;
      }

      this.$router.replace({ query }).catch(() => {});
    },

    handleProfileMenuSelect(value) {
      this.profileMenuOpen = false;

      if (value === 'unmatch') {
        this.unmatchProfile();
      } else if (value === 'delete') {
        this.deleteProfile();
      }
    },

    onExcludeFromMatchesIndicatorClick() {
      if (!this.isStaff) return;
      this.includeProfileInMatchQueueAgain();
    },

    async includeProfileInMatchQueueAgain() {
      if (!this.isStaff || !this.profileId || this.profile?.excludeFromMatches !== true) return;

      const profileDocId = await this.resolveCanonicalProfileId(this.profileId);
      const ok = window.confirm(
        'Include this profile in the unmatched match queue again? It will appear when searching for profiles that still need matching.',
      );
      if (!ok) return;

      this.restoringToMatchQueue = true;
      try {
        const { data } = await firebase.functions().httpsCallable('httpexcludeprofilefrommatch')({
          profileId: profileDocId,
          excludeFromMatches: false,
        });

        const updated = data.updated || 0;
        const skipped = Array.isArray(data.skipped) ? data.skipped : [];

        if (updated < 1) {
          const reason = skipped[0]?.reason || 'unknown';
          this.$toasted.error(
            reason === 'merged'
              ? 'This profile is merged; un-exclude is not applied.'
              : reason === 'redirected'
                ? 'This profile redirects elsewhere; un-exclude is not applied.'
                : reason === 'not_found'
                  ? 'Profile was not found.'
                  : 'Could not update this profile.',
          );
          return;
        }

        this.$set(this.profile, 'excludeFromMatches', false);
        for (const key of profileCache.keys()) {
          if (key === `${profileDocId}:default` || key.startsWith(`${profileDocId}:`)) {
            profileCache.delete(key);
          }
        }
        this.$toasted.success('Profile is included in the match queue again');
      } catch (e) {
        this.$toasted.error(e.message || 'Failed to update profile');
      } finally {
        this.restoringToMatchQueue = false;
      }
    },

    async unmatchProfile() {
      if (!window.confirm('Unmatch this profile? Solo profiles will be cleared of the canonical link and the merged profile will be deleted.')) return;
      try {
        await firebase.firestore().collection('profiles').doc(this.profileId).delete();
        this.$toasted.success('Profile unmatched');

        setTimeout(() => {
          this.$router.push('/profiles');
        }, 3000);
      } catch (e) {
        this.$toasted.error(e.message || 'Failed to unmatch profile');
      }
    },

    async deleteProfile() {
      if (!window.confirm('Permanently delete this profile? This cannot be undone.')) return;
      try {
        await firebase.firestore().collection('profiles').doc(this.profileId).delete();
        this.$toasted.success('Profile deleted');

        setTimeout(() => {
          this.$router.push('/profiles');
        }, 3000);
      } catch (e) {
        this.$toasted.error(e.message || 'Failed to delete profile');
      }
    },

    close() {
      const query = { ...this.$route.query };
      delete query.profileId;
      delete query.profileGrader;
      delete query.profileGrade;
      delete query.profilePopulationType;
      this.$router.push({ query }).catch(() => {});
    },

    clearSelectionParamsFromRoute() {
      // Only mutate the route if we're currently the active profile panel.
      if (this.$route?.query?.profileId !== this.profileId) return;

      const query = { ...this.$route.query };

      if (query.profileGrader == null && query.profileGrade == null && query.profilePopulationType == null) return;

      delete query.profileGrader;
      delete query.profileGrade;
      delete query.profilePopulationType;

      this.$router.replace({ query }).catch(() => {});
    },

    fullscreen() {
      if (!this.profileId) return;

      this.$router.push({
        path: `/profiles/${this.profileId}`,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.profile-profile {
  min-height: 100%;

  &:not(.page) {
    padding: $gp;

    .stats-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .profile-attributes {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .profile-header-image {
      width: 100%;
      max-width: none;
      align-self: stretch;
    }

    .profile-header-stats {
      grid-template-columns: repeat(2, 1fr);
    }

    .grader-stats {
      grid-template-columns: repeat(2, 1fr);
    }

    .grades-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }
}

.loading {
  display: flex;
  justify-content: center;
  padding: $gp * 4;
}

.error {
  color: var(--theme-color);
  padding: $gp;
}

.profile-data {
  margin-top: $gp;
}

.not-found {
  padding: $gp;
  color: var(--theme-inactive);
}

.profile-header {
  position: relative;
  display: grid;
  grid-gap: $gp;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 2fr);
  margin-bottom: $gp * 2;
  border-bottom: solid 1px var(--theme-border);
  padding-bottom: $gp * 2;

  &:not(.page) {
    flex-direction: column;
    grid-template-columns: repeat(1, 1fr);
  }

  @media($medium-down) {
    flex-direction: column;
    grid-template-columns: repeat(1, 1fr);
  }
}

.profile-header--no-image {
  grid-template-columns: minmax(0, 1fr);
}

.profile-header-image {
  align-self: center;
  max-width: 360px;

  @media($medium-down) {
    width: 100%;
    max-width: none;
    align-self: stretch;
  }
}

.profile-header-info {
  display: flex;
  flex-direction: column;
  gap: $gp;
}

.profile-header-title h3 {
  margin-bottom: $gp * .5;
}

.profile-graders {
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: $gp * .5;
}

.profile-excluded-indicator.icon-button {
  margin-left: 2px;
  align-self: center;
  --theme-icon-button-color: #{$color-gray-6};
}

.profile-excluded-indicator--staff-action.icon-button {
  cursor: pointer;
}

.profile-grader-logo-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-radius: 999px;
  padding: 6px 12px;
  border: 2px solid var(--theme-border);
}

.profile-grader-logo-pill--interactive {
  cursor: pointer;
  transition: box-shadow 0.15s ease, transform 0.15s ease;

  &:hover {
    box-shadow: 0 0 0 2px var(--theme-active);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--theme-active);
  }
}

.profile-grader-logo-pill--selected {
  border-color: $color-green-1;
}

.profile-grader-logo-img {
  display: block;
  max-height: 22px;
  width: auto;
  max-width: 88px;
  object-fit: contain;
}

.grader-section-header-brand {
  display: flex;
  align-items: center;
  min-width: 0;
}

.profile-header-stats {
  align-self: stretch;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  max-width: none;
}

.profile-stat {
  padding: $gp * .75;
  border-radius: $border-radius;
  background-color: rgba(255, 255, 255, 0.04);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.04);
}

.profile-attributes {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-gap: $gp * .75 $gp * 1.5;
  font-size: 13px;

  @media($large-down) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.profile-attribute {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.profile-attribute-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  opacity: 0.7;
}

.profile-attribute-value {
  font-size: 14px;
  font-weight: 600;
}

.profile-view-all-sales-cta {
  margin-top: $gp * 1.25;
  align-self: stretch;
}

.profile-header-actions {
  grid-column: 1 / -1;
}

.index-container {
  cursor: pointer;
  padding: $gp;
  margin-bottom: $gp * 2;
  border-bottom: solid 1px var(--theme-border);

  &:hover {
    background-color: var(--theme-highlight);
  }
}

.stats-grid {
  display: grid;
  grid-gap: $gp $gp * .5;
  grid-template-columns: repeat(4, 1fr);

  @media($medium-down) {
    grid-template-columns: repeat(2, 1fr);
  }

  &.page {
    @media($xlarge-down) {
      grid-template-columns: repeat(3, 1fr);
    }

    @media($large-down) {
      grid-template-columns: repeat(2, 1fr);
    }
  }
}

.graders-section {
  padding-bottom: $gp * 2;
  border-bottom: solid 1px var(--theme-border);
}

.graders-section-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: $gp;
  margin-bottom: $gp;
}

.graders-section-header h4 {
  margin: 0;
}

.population-report-value-dropdown {
  flex-shrink: 0;
}

.grader-section:not(:last-child) {
  margin-bottom: $gp * 2;
}

.grader-section-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: $gp;
  margin-bottom: $gp;
}

.grader-section-header h5 {
  margin: 0;
}

.grader-population-type-dropdown {
  flex-shrink: 0;
}

.grader-stats {
  display: grid;
  grid-gap: $gp * .5;
  grid-template-columns: repeat(3, 1fr);
  margin-bottom: $gp;
}

.profile-sales-row {
  margin-bottom: $gp * 2;
  border-bottom: solid 1px var(--theme-border);
  padding-bottom: $gp * 2;

  h4 {
    margin: 0;
  }
}

.profile-profile.page .profile-sales-row {
  scroll-margin-top: $gp * 2;
}

.profile-sales-filters {
  margin-top: $gp;
  flex-wrap: nowrap;
  align-items: center;

  > * {
    flex: 0 0 auto;
  }
}

.profile-sales-filter-dropdown {
  flex-shrink: 0;
}

.profile-recent-sales {
  margin-top: $gp;

  :deep(.list-item) {
    padding-left: 0;
    padding-right: 0;
  }
}

.grades-grid {
  display: grid;
  grid-gap: $gp * .5;
  grid-template-columns: repeat(8, 1fr);

  @media($large-down) {
    grid-template-columns: repeat(6, 1fr);
  }

  @media($medium-down) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media($small-down) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.grade-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 72px;
  padding: $gp * 0.75 $gp * 0.5;
  border-radius: $border-radius;
  border: solid 1px var(--theme-input-border);
  background-color: var(--theme-background);
  color: var(--theme-color);
  text-align: center;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background-color: var(--theme-highlight);
  }

  &.selected {
    border-color: var(--theme-color);
    background-color: var(--theme-highlight);
  }

  &.no-sales {
    cursor: not-allowed;
    opacity: 0.85;
    pointer-events: none;

    &:hover {
      background-color: var(--theme-background);
    }

    .grade-metric--price .grade-metric-value {
      opacity: 0.75;
    }
  }
}

.grade-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--theme-color);
  margin-bottom: $gp * 0.5;
  line-height: 1.2;
}

.grade-metrics {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $gp * 0.75;
  width: 100%;
}

.grade-metric {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  color: var(--theme-color);

  &--price .grade-metric-value {
    font-size: 15px;
    font-weight: 700;
    color: var(--theme-color);
    line-height: 1.2;
  }

  &--pop .grade-metric-value {
    font-size: 15px;
    font-weight: 700;
  }
}

.grade-metric-label {
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--theme-color);
  opacity: 0.8;
  line-height: 1;
}

.profile-header-info {
  h2 {
    margin-bottom: $gp * .5;
  }
}

</style>
